import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOMER_IMPORT_FIELD_DEFINITIONS,
  IGNORED_COLUMN,
  missingRequiredFields,
  type ImportTarget,
  type MeasurementColumnCandidate,
} from '@tailoring-crm/shared';

/**
 * "Which of your columns is the phone number?"
 *
 * The guess is already applied when this renders, so most files need the owner to read one
 * screen and press one button. What they are reading is a table of THEIR headings with a
 * sample of THEIR data beside each one — not a list of our field names — because the question
 * is about their file and they should be able to answer it by recognising a row.
 *
 * A field can only be claimed by one column: choosing it somewhere else releases it from where
 * it was, rather than silently letting two columns both write the customer's name.
 */

export interface ColumnMapperProps {
  header: readonly string[];
  /** One row of real data, so a heading like "Col 3" can still be identified. */
  sample: readonly string[];
  mapping: readonly ImportTarget[];
  measurementFields: readonly MeasurementColumnCandidate[];
  onChange: (mapping: ImportTarget[]) => void;
}

const IGNORE_VALUE = 'ignore';

function valueOf(target: ImportTarget | undefined): string {
  if (!target || target.kind === 'ignore') return IGNORE_VALUE;
  return target.kind === 'field' ? `field:${target.field}` : `measurement:${target.fieldKey}`;
}

function parseValue(value: string): ImportTarget {
  if (value.startsWith('field:')) {
    return { kind: 'field', field: value.slice('field:'.length) } as ImportTarget;
  }
  if (value.startsWith('measurement:')) {
    return { kind: 'measurement', fieldKey: value.slice('measurement:'.length) };
  }
  return IGNORED_COLUMN;
}

export function ColumnMapper({
  header,
  sample,
  mapping,
  measurementFields,
  onChange,
}: ColumnMapperProps): ReactElement {
  const { t } = useTranslation();
  const missing = missingRequiredFields(mapping);

  function choose(column: number, value: string): void {
    const target = parseValue(value);
    const next = header.map((_, index) => mapping[index] ?? IGNORED_COLUMN);
    next[column] = target;

    // Release the same destination from any other column — one field, one column.
    if (target.kind !== 'ignore') {
      const chosen = valueOf(target);
      for (let index = 0; index < next.length; index += 1) {
        if (index !== column && valueOf(next[index]) === chosen) next[index] = IGNORED_COLUMN;
      }
    }
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-4">
      {missing.length > 0 ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
          {t('import.mapping.missing', {
            fields: missing.map((field) => t(`import.field.${field}`)).join(', '),
          })}
        </p>
      ) : null}

      <ul className="flex flex-col gap-2">
        {header.map((heading, column) => (
          <li
            key={`${heading}-${String(column)}`}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {heading.length > 0
                  ? heading
                  : t('import.mapping.unnamedColumn', { number: column + 1 })}
              </p>
              <p className="truncate text-sm text-muted-foreground">
                {sample[column] ? t('import.mapping.example', { value: sample[column] }) : ''}
              </p>
            </div>

            <label className="sr-only" htmlFor={`import-column-${String(column)}`}>
              {t('import.mapping.selectLabel', { heading })}
            </label>
            <select
              id={`import-column-${String(column)}`}
              value={valueOf(mapping[column])}
              onChange={(event) => choose(column, event.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value={IGNORE_VALUE}>{t('import.mapping.ignore')}</option>
              <optgroup label={t('import.mapping.customerFields')}>
                {CUSTOMER_IMPORT_FIELD_DEFINITIONS.map((definition) => (
                  <option key={definition.key} value={`field:${definition.key}`}>
                    {t(`import.field.${definition.key}`)}
                    {definition.required ? ' *' : ''}
                  </option>
                ))}
              </optgroup>
              {measurementFields.length > 0 ? (
                <optgroup label={t('import.mapping.measurementFields')}>
                  {measurementFields.map((field) => (
                    <option key={field.fieldKey} value={`measurement:${field.fieldKey}`}>
                      {field.label}
                    </option>
                  ))}
                </optgroup>
              ) : null}
            </select>
          </li>
        ))}
      </ul>
    </div>
  );
}
